/**
 * Importable labour / material candidates for Add Line Item.
 * Reuses the same unallocated seed path as Refresh from Project,
 * but appends only lines not already on the current draft.
 */

import {
  getUnallocatedMaterialUsage,
  getUnallocatedTimeLogs,
} from "./invoiceAllocation";
import { billingPeriodOptionsFromInvoice } from "./invoiceReport";
import {
  buildSeedLineFromMaterialUsage,
  buildSeedLineFromTimeLog,
  loadCatalogSellByMaterialId,
} from "./invoiceReview";
import { findMatchingQuoteLine } from "./projectQuoteLineMatching";
import { loadProjectQuoteLines } from "./projectQuoteSnapshots";

function resolveImportPeriod(invoice) {
  const options = billingPeriodOptionsFromInvoice(invoice);
  if (options.billingPeriodMode === "custom_range") {
    return {
      startDate: options.billingPeriodStart || null,
      endDate: options.billingPeriodEnd || null,
    };
  }
  return { startDate: null, endDate: null };
}

/** Source IDs already on the current draft (blocks re-import until hard-removed). */
export function existingSourceTimeLogIds(lineItems = []) {
  return new Set(
    (lineItems || []).map((line) => line.source_time_log_id).filter(Boolean),
  );
}

export function existingSourceUsageIds(lineItems = []) {
  return new Set(
    (lineItems || []).map((line) => line.source_usage_id).filter(Boolean),
  );
}

/**
 * @returns {Promise<Array<{ key: string, label: string, detail: string, seedLine: object }>>}
 */
export async function loadImportableLabourCandidates(
  actor,
  invoice,
  lineItems = [],
) {
  if (!invoice?.project_id) return [];

  const { startDate, endDate } = resolveImportPeriod(invoice);
  const [timeLogs, quoteLines] = await Promise.all([
    getUnallocatedTimeLogs(invoice.project_id, {
      startDate,
      endDate,
      actor,
    }),
    loadProjectQuoteLines(invoice.project_id),
  ]);

  const alreadyOnInvoice = existingSourceTimeLogIds(lineItems);

  return timeLogs
    .filter((log) => log?.id && !alreadyOnInvoice.has(log.id))
    .map((log) => {
      const quoteLine = findMatchingQuoteLine(quoteLines, {
        lineType: "labour",
        quoteLineSnapshotId: log.quote_line_snapshot_id,
      });
      const seedLine = buildSeedLineFromTimeLog(log, { quoteLine, sortOrder: 0 });
      return {
        key: String(log.id),
        label: seedLine.description,
        detail: seedLine.detail,
        seedLine,
      };
    });
}

/**
 * @returns {Promise<Array<{ key: string, label: string, detail: string, seedLine: object }>>}
 */
export async function loadImportableMaterialCandidates(
  actor,
  invoice,
  lineItems = [],
) {
  if (!invoice?.project_id) return [];

  const { startDate, endDate } = resolveImportPeriod(invoice);
  const materialRows = await getUnallocatedMaterialUsage(invoice.project_id, {
    startDate,
    endDate,
    actor,
  });

  const alreadyOnInvoice = existingSourceUsageIds(lineItems);
  const available = materialRows.filter(
    (row) => row?.usage_id && !alreadyOnInvoice.has(row.usage_id),
  );

  if (!available.length) return [];

  const [quoteLines, catalogByMaterialId] = await Promise.all([
    loadProjectQuoteLines(invoice.project_id),
    loadCatalogSellByMaterialId(
      [
        ...new Set(
          available.map((row) => row.material_id).filter(Boolean),
        ),
      ],
      actor,
    ),
  ]);

  return available.map((row) => {
    const quoteLine =
      findMatchingQuoteLine(quoteLines, {
        lineType: "material",
        materialId: row.material_id,
        quoteLineSnapshotId: row.quote_line_snapshot_id,
      }) ||
      findMatchingQuoteLine(quoteLines, {
        lineType: "material",
        quoteLineSnapshotId: row.quote_line_snapshot_id,
      });

    const catalogSell = row.material_id
      ? catalogByMaterialId.get(row.material_id)?.sell_price
      : null;

    const seedLine = buildSeedLineFromMaterialUsage(row, {
      quoteLine,
      catalogSell,
      sortOrder: 0,
    });

    return {
      key: String(row.usage_id),
      label: seedLine.description,
      detail: seedLine.detail,
      seedLine,
    };
  });
}
